import type { RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import { SOFT_LINE_BREAK, TEXT_LAYOUT_PARAGRAPHS, textLayoutAtLeast } from '@lite-workspace/text-layout';
import { CARET_BASELINE_DESCENT_RATIO } from './caret-metrics';
import { planTextLayout, runCapsDivergeFromBox, stacksUnevenLines, type TextLinePlan } from './glyph-layout-backend';
import type { Shaper } from './glyph-shaper';
import { wrapZonesCrossFrame } from './text-paragraphs';

/**
 * Сетка каретки для Area Type on Shape (FKS-11074).
 *
 * Инлайн-редактор — прозрачная `<textarea>`, и браузер верстает её по ПРЯМОУГОЛЬНИКУ рамки, а движок
 * заливает area-текст по полосам контура (своя ширина и свой левый край на каждой строке). Сетки строк
 * расходятся, поэтому браузерная каретка встаёт в чужое место, а клик отдаёт чужой индекс. Здесь строится
 * вторая проекция ТОЙ ЖЕ раскладки, по которой рисуются глифы (`planTextLayout`), но в терминах
 * «индекс символа ↔ позиция каретки»: textarea остаётся приёмником ввода, а видимую каретку рисуем сами.
 *
 * Координаты — canvas-px ОТНОСИТЕЛЬНО левого верхнего угла `box.rect` (потребитель домножает на масштаб
 * экрана), чтобы модуль ничего не знал ни о камере, ни о DOM.
 */

export interface AreaCaretLine {
  /** Начало строки в ИСХОДНОМ тексте. */
  charStart: number;
  /** Конец строки в исходном тексте (`charStart + text.length`). */
  charEnd: number;
  text: string;
  /** Верх каретки на этой строке (box-local canvas px). */
  top: number;
  /** Низ каретки на этой строке (box-local canvas px). */
  bottom: number;
  /**
   * Строка может принять каретку. `false` — пустая строка-заглушка мёртвой полосы контура (`wrapLinesShaped`
   * проматывает полосы вне фигуры пустыми строками): символов она не держит, и клик/стрелки должны её пройти.
   */
  holdsCaret: boolean;
  /**
   * Ширина полосы подсветки ЗА концом строки (box-local canvas px) — браузер красит и сам перевод
   * строки. `0` у мягкого переноса и у последней строки: там за концом нет символа.
   */
  newlineBandWidth: number;
}

export interface AreaCaretLayout {
  lines: AreaCaretLine[];
  /** x каретки после `offset` символов строки `lineIndex` (box-local canvas px). */
  caretX(lineIndex: number, offset: number): number;
}

export interface AreaCaretSlot {
  lineIndex: number;
  x: number;
  top: number;
  bottom: number;
}

function caretLayoutFromPlan(box: RenderTextBox, plan: TextLinePlan): AreaCaretLayout {
  // Движок кладёт глифы от округлённого угла бокса; каретка обязана повторить тот же сдвиг.
  const originX = plan.canvasLeft - box.rect.x;
  const originY = plan.canvasTop - box.rect.y + plan.verticalOffset;
  const capPx = plan.capHeightPx * plan.vScale * plan.scaleY;
  const descentPx = plan.fontSize * CARET_BASELINE_DESCENT_RATIO * plan.vScale * plan.scaleY;

  // Дефис переноса по слогам рисуется, но символом текста не является: каретка его не проходит.
  const sourceText = (line: TextLinePlan['lines'][number]): string => (line.hyphenated ? line.text.slice(0, -1) : line.text);
  const lines: AreaCaretLine[] = plan.lines.map((line, i) => {
    const baseline =
      originY + (line.baseline - plan.yGap - plan.ascenderDelta - plan.baselineLift + plan.baseShift) * plan.scaleY;
    const next = plan.lines[i + 1];
    // Огрызок есть только там, где за концом строки стоит настоящий символ. Кегль берём В ТОЧКЕ перевода:
    // у многорангового текста ран следующей строки может быть мельче, и полоса поехала бы шире глифов.
    const text = sourceText(line);
    const lineEnd = line.charStart + text.length;
    const hardBreak = line.paragraphEnd && next !== undefined;
    return {
      charStart: line.charStart,
      charEnd: lineEnd,
      text,
      top: baseline - capPx,
      bottom: baseline + descentPx,
      holdsCaret: text.length > 0 || next === undefined || next.charStart > line.charStart,
      newlineBandWidth: hardBreak ? plan.measurePrefixAdvance(' ', lineEnd) * plan.scaleX : 0,
    };
  });

  const caretX = (lineIndex: number, offset: number): number => {
    const line = plan.lines[lineIndex];
    if (!line) return originX;
    const text = sourceText(line);
    const prefix = text.slice(0, Math.max(0, Math.min(offset, text.length)));
    const advance = prefix.length > 0 ? plan.measurePrefixAdvance(prefix, line.charStart) : 0;
    // Выключка раздвигает всё, что правее каждого пройденного пробела — каретка едет вместе с глифами.
    let spaces = 0;
    if (line.justifyDelta !== 0) for (const ch of prefix) if (plan.isJustifyGap(ch)) spaces += 1;
    return originX + (line.xOffset + advance + line.justifyDelta * spaces) * plan.scaleX;
  };

  return { lines, caretX };
}

/**
 * Сетка каретки бокса. `undefined` — раскладку бокса браузер воспроизводит сам (текст, прижатый к
 * левому краю): каретку по-прежнему ведёт textarea, поведение не меняется.
 *
 * Признак — КЛАСС бокса, а не его текущий текст. Хвостовой пробел движок вешает за краем строки, а
 * браузер его меряет и уводит строку влево на его ширину; появиться такой пробел может в любой момент
 * набора, поэтому у прижатого вправо или по центру бокса сетку движка держим всю сессию. Проверка «а
 * есть ли пробел прямо сейчас» отдала бы каретку браузеру ровно до того момента, когда её уже поздно
 * забирать: слушатели и погашенная подсветка ставятся один раз, на входе в редактирование.
 *
 * `force` удерживает сессию на сетке движка и для контурных боксов, где признак зависит от текста.
 *
 * Многоранговый текст (`box.runs`) меряется ТЕМ ЖЕ пером, что и multi-run отрисовка (кегль, трекинг,
 * кернинг на стыке ранов, капсы): ран со своим кеглем сдвигает перо на ПОЛНЫЕ ширины глифов, и мера
 * box-метриками увела бы и каретку, и разбор клика на несколько символов. Box-метрикой остаётся только
 * бюджет ПЕРЕНОСА — там сетка строк от ранов не зависит (зеркально Rust `layout_multi_run`).
 */
// Раскладки, которые textarea не повторит НИ ПРИ КАКОМ тексте: right/center вешают хвостовой пробел за
// край строки, а justify раздвигает межсловные интервалы. Выключке контур не нужен — движок делает её и
// по рамке бокса, поэтому предикат здесь тот же, что и у `justifyDeltasForLines`: align + textMode.
// Перенос по слогам textarea тоже не повторит: браузер переносит слово целиком.
// Вертикальный масштаб textarea тянет вместе с буквами и межстрочный интервал, а движок интервал не трогает.
// Отбивку абзацев и интерлиньяж ранов textarea не знает: у неё один line-height на весь текст.
// Регистр у textarea тоже один на всё поле: заглавный ран среди строчных она верстает уже, чем движок.
function alignGridDiverges(box: RenderTextBox): boolean {
  return (
    box.align === 'right' ||
    box.align === 'center' ||
    (box.verticalScale ?? 1) !== 1 ||
    stacksUnevenLines(box) ||
    runCapsDivergeFromBox(box) ||
    paragraphsDiverge(box) ||
    (box.textMode === 'block' && (box.align === 'justify' || box.autoHyphenate === true))
  );
}

// Абзацы Photoshop (textLayout 7) textarea не знает: свои отступы и выравнивание у каждого абзаца, отступ первой строки
// у КАЖДОГО абзаца (text-indent — только у первой строки поля), мягкий перенос U+2028 и перенос по кеглю каждого рана.
function paragraphsDiverge(box: RenderTextBox): boolean {
  if (!textLayoutAtLeast(box.textLayout, TEXT_LAYOUT_PARAGRAPHS)) return false;
  return (
    (box.paragraphRuns?.length ?? 0) > 0 ||
    (box.text ?? '').includes(SOFT_LINE_BREAK) ||
    (box.firstLineIndent ?? 0) !== 0 ||
    (box.textMode === 'block' && (box.runs?.length ?? 0) > 1)
  );
}

const onContour = (box: RenderTextBox): boolean => box.textMode === 'block' && !!box.areaShape?.d;

/** Lines set around objects across the frame (`wrapZones`): one baseline may hold a segment on each side of one. */
const aroundObjects = (box: RenderTextBox): boolean => box.textMode === 'block' && wrapZonesCrossFrame(box);

/** The textarea cannot lay this box out the way the engine does — the caret has to follow the engine's grid. */
export function caretGridDiverges(box: RenderTextBox): boolean {
  return onContour(box) || aroundObjects(box) || alignGridDiverges(box);
}

export function buildAreaCaretLayout(
  box: RenderTextBox,
  shaper: Shaper,
  fontRef: string,
  force = false,
): AreaCaretLayout | undefined {
  const contour = onContour(box) || aroundObjects(box);
  const gridDiverges = alignGridDiverges(box);
  if (!force && !contour && !gridDiverges) return undefined;
  const plan = planTextLayout(box, shaper, fontRef);
  if (!plan || plan.lines.length === 0) return undefined;
  if (force || gridDiverges) return caretLayoutFromPlan(box, plan);
  // Непригодный контур (ноль живых полос) — движок переносит такой бокс по рамке, и браузерная каретка
  // ему подходит: выключку `gridDiverges` забрал выше, сюда доходит только прижатый влево текст.
  if (!plan.shaped) return undefined;
  return caretLayoutFromPlan(box, plan);
}

/** Полоса подсветки выделения на одной строке (box-local canvas px). */
export interface AreaCaretBand {
  x: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Полосы выделения `[from; to)` по строкам сетки движка. Браузер рисует свою подсветку по СВОЕЙ
 * раскладке — там, где сетки расходятся, она уезжает вместе с выделенным текстом, поэтому оверлей
 * гасит её и кладёт эти полосы.
 */
export function caretSelectionBands(layout: AreaCaretLayout, from: number, to: number): AreaCaretBand[] {
  const start = Math.min(from, to);
  const end = Math.max(from, to);
  if (start === end) return [];
  const bands: AreaCaretBand[] = [];
  for (let i = 0; i < layout.lines.length; i++) {
    const line = layout.lines[i];
    if (!line.holdsCaret) continue;
    const lo = Math.max(start, line.charStart);
    const hi = Math.min(end, line.charEnd);
    if (hi < lo) continue;
    // Выделение уходит за конец строки — значит съело и её перевод. Без этого хвоста пустая строка внутри
    // выделения оставалась бы дыркой в подсветке: своей ширины у неё нет.
    const breakWidth = end > line.charEnd ? line.newlineBandWidth : 0;
    if (hi === lo && breakWidth === 0) continue;
    const x0 = layout.caretX(i, lo - line.charStart);
    const x1 = layout.caretX(i, hi - line.charStart);
    bands.push({
      x: Math.min(x0, x1),
      top: line.top,
      width: Math.abs(x1 - x0) + breakWidth,
      height: line.bottom - line.top,
    });
  }

  return bands;
}

/** What the line lookup needs of a caret line — the engine grid shares it. */
export type CaretLineSpan = Pick<AreaCaretLine, 'charStart' | 'charEnd' | 'holdsCaret'>;

function selectableLines(layout: { lines: readonly CaretLineSpan[] }): number[] {
  const out: number[] = [];
  for (let i = 0; i < layout.lines.length; i++) if (layout.lines[i].holdsCaret) out.push(i);
  return out.length > 0 ? out : layout.lines.map((_, i) => i);
}

/** Строка, на которой стоит каретка с индексом `index`. */
export function caretLineForIndex(layout: { lines: readonly CaretLineSpan[] }, index: number): number {
  const usable = selectableLines(layout);
  for (let k = 0; k < usable.length; k++) {
    const line = layout.lines[usable[k]];
    if (index > line.charEnd) continue;
    if (index < line.charEnd) return usable[k];
    // Ровно на границе мягкого переноса (следующая строка начинается ТЕМ ЖЕ символом) каретка
    // принадлежит СЛЕДУЮЩЕЙ строке: иначе клик в её начало рисовал бы каретку в конце предыдущей.
    const next = layout.lines[usable[k + 1]];
    if (next === undefined || next.charStart > index) return usable[k];
  }

  return usable[usable.length - 1];
}

/** Ближайшая к `x` граница символа внутри строки, в символах от её начала. */
function offsetForX(layout: AreaCaretLayout, lineIndex: number, x: number): number {
  const len = layout.lines[lineIndex]?.text.length ?? 0;
  if (len === 0) return 0;
  // caretX монотонна по offset — бинарный поиск последней границы левее x, затем выбор ближайшей из пары.
  let lo = 0;
  let hi = len;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (layout.caretX(lineIndex, mid) <= x) lo = mid;
    else hi = mid - 1;
  }

  if (lo >= len) return len;
  return Math.abs(x - layout.caretX(lineIndex, lo + 1)) < Math.abs(x - layout.caretX(lineIndex, lo)) ? lo + 1 : lo;
}

export function caretSlotAt(layout: AreaCaretLayout, index: number): AreaCaretSlot | undefined {
  if (layout.lines.length === 0) return undefined;
  const lineIndex = caretLineForIndex(layout, index);
  const line = layout.lines[lineIndex];
  // Перенос съедает ведущие пробелы продолжения: индекс внутри съеденного куска клампится к началу строки.
  const offset = Math.max(0, Math.min(index - line.charStart, line.text.length));
  return { lineIndex, x: layout.caretX(lineIndex, offset), top: line.top, bottom: line.bottom };
}

/** Lines on one baseline (beside an object that lets text through on both sides) share their top. */
const sameRow = (a: AreaCaretLine, b: AreaCaretLine): boolean => Math.abs(a.top - b.top) < 1e-6;

/** How far `x` is from the span line `i` sets (0 inside it). */
function xDistance(layout: AreaCaretLayout, i: number, x: number): number {
  const left = layout.caretX(i, 0);
  const right = layout.caretX(i, layout.lines[i].text.length);
  return x < left ? left - x : x > right ? x - right : 0;
}

/** Индекс символа под точкой (box-local canvas px) — маппинг клика в текст. */
export function caretIndexAt(layout: AreaCaretLayout, x: number, y: number): number {
  const usable = selectableLines(layout);
  if (usable.length === 0) return 0;
  let best = usable[0];
  let bestDistance = Infinity;
  for (const i of usable) {
    const line = layout.lines[i];
    const distance = y < line.top ? line.top - y : y > line.bottom ? y - line.bottom : 0;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    } else if (distance === bestDistance && sameRow(line, layout.lines[best])) {
      // Beside an object: the segment of the row under the pointer.
      if (xDistance(layout, i, x) < xDistance(layout, best, x)) best = i;
    }
  }

  return layout.lines[best].charStart + offsetForX(layout, best, x);
}

/**
 * Индекс на строку выше/ниже с сохранением колонки `desiredX`. `undefined` — шагать некуда
 * (первая/последняя строка): caller оставляет клавишу браузеру.
 */
export function caretIndexByLineStep(
  layout: AreaCaretLayout,
  index: number,
  delta: -1 | 1,
  desiredX?: number,
): number | undefined {
  const usable = selectableLines(layout);
  const current = caretLineForIndex(layout, index);
  const position = usable.indexOf(current);
  if (position < 0) return undefined;
  // The next row up/down: a segment beside an object on the same baseline is not a line step.
  let k = position + delta;
  while (usable[k] !== undefined && sameRow(layout.lines[usable[k]], layout.lines[current])) k += delta;
  const first = usable[k];
  if (first === undefined) return undefined;
  const x = desiredX ?? caretSlotAt(layout, index)?.x ?? 0;
  let target = first;
  for (let j = k + delta; usable[j] !== undefined && sameRow(layout.lines[usable[j]], layout.lines[first]); j += delta) {
    if (xDistance(layout, usable[j], x) < xDistance(layout, target, x)) target = usable[j];
  }

  return layout.lines[target].charStart + offsetForX(layout, target, x);
}

/** Начало/конец ВИЗУАЛЬНОЙ строки, на которой стоит каретка (Home/End). */
export function caretLineEdge(layout: AreaCaretLayout, index: number, edge: 'start' | 'end'): number {
  const line = layout.lines[caretLineForIndex(layout, index)];
  return edge === 'start' ? line.charStart : line.charEnd;
}
